import { action, makeObservable, observable } from 'mobx';
import { AttachmentsPanelType } from './types';
import { ATTACHMENT_CREATED_WEBSOCKET_MESSAGE_TIMEOUT_FOR_UPDATE_UPLOAD_PROGRESS } from './constants';

export type UploadStatus = 'uploading' | 'processing' | 'completed' | 'failed';

export interface AttachmentUpload {
    id: string; // fileServerFileId from backend, used as primary identifier
    entityId: number;
    entityType: AttachmentsPanelType;
    entityNumber?: string; // agencyEventNumber for events, boloNumber for BOLOs
    fileName: string;
    fileSize: number;
    uploadedBytes: number;
    status: UploadStatus;
    startTime: Date;
    completedTime?: Date;
    error?: string;
}

export class AttachmentsUploadStore {
    @observable
    private uploads: Map<string, AttachmentUpload> = new Map();

    private timeouts: Map<string, NodeJS.Timeout> = new Map();

    constructor() {
        makeObservable(this);
    }

    @action
    addUpload(
        fileServerFileId: string,
        upload: Omit<AttachmentUpload, 'id' | 'startTime' | 'uploadedBytes' | 'status'>
    ): void {
        const newUpload: AttachmentUpload = {
            ...upload,
            id: fileServerFileId,
            startTime: new Date(),
            uploadedBytes: 0,
            status: 'uploading',
        };
        this.uploads.set(fileServerFileId, newUpload);
    }

    /**
     * Convenience method to add an upload that has already failed.
     */
    @action
    addFailedUpload(
        fileServerFileId: string,
        upload: Omit<AttachmentUpload, 'id' | 'startTime' | 'uploadedBytes' | 'status' | 'error'>,
        error: string
    ): void {
        const failedUpload: AttachmentUpload = {
            ...upload,
            id: fileServerFileId,
            startTime: new Date(),
            completedTime: new Date(),
            uploadedBytes: 0,
            status: 'failed',
            error,
        };
        this.uploads.set(fileServerFileId, failedUpload);
    }

    @action
    updateUploadProgress(
        { id, uploadedBytes }: { id: string; uploadedBytes: number },
        processingStateTimeoutCallback?: () => void
    ): void {
        const upload = this.uploads.get(id);
        if (upload && upload.status === 'uploading') {
            upload.uploadedBytes = uploadedBytes;

            // If file is fully uploaded to backend, switch to processing status
            if (uploadedBytes >= upload.fileSize) {
                upload.status = 'processing';
                this.setTimeoutForUpload(
                    id,
                    ATTACHMENT_CREATED_WEBSOCKET_MESSAGE_TIMEOUT_FOR_UPDATE_UPLOAD_PROGRESS,
                    processingStateTimeoutCallback
                );
            }
        }
    }

    @action
    markUploadCompleted(id: string): void {
        const upload = this.uploads.get(id);
        if (upload) {
            upload.status = 'completed';
            upload.completedTime = new Date();
        }
    }

    @action
    markUploadFailed(id: string, error: string): void {
        const upload = this.uploads.get(id);
        if (upload) {
            upload.status = 'failed';
            upload.error = error;
            upload.completedTime = new Date();
        }
    }

    @action
    removeUpload(fileServerFileId: string): void {
        this.clearTimeout(fileServerFileId);
        this.uploads.delete(fileServerFileId);
    }

    getUpload(id: string): AttachmentUpload | undefined {
        return this.uploads.get(id);
    }

    getAllUploads(): AttachmentUpload[] {
        return Array.from(this.uploads.values());
    }

    getUploadsForEntity(entityId: number, entityType: AttachmentsPanelType): AttachmentUpload[] {
        return Array.from(this.uploads.values()).filter(
            (upload) => upload.entityId === entityId && upload.entityType === entityType
        );
    }

    getActiveUploads(): AttachmentUpload[] {
        return Array.from(this.uploads.values()).filter(
            (upload) => upload.status === 'uploading' || upload.status === 'processing'
        );
    }

    /**
     * Get count of active uploads (uploading or processing)
     * Used to enforce concurrent upload limits
     */
    getActiveUploadCount(): number {
        return this.getActiveUploads().length;
    }

    /**
     * Calculate upload progress percentage
     */
    getUploadProgressPercentage(id: string): number {
        const upload = this.uploads.get(id);
        if (!upload) return 0;

        if (upload.status === 'completed') return 100;
        if (upload.status === 'failed') return 0;
        if (upload.status === 'processing') return 99;

        // FE to BE upload progress (0-99%)
        return Math.min((upload.uploadedBytes / upload.fileSize) * 99, 99);
    }

    /**
     * Handle socket message for upload completion by fileServerFileId
     */
    @action
    handleUploadCompletionByFileServerFileId(message: {
        fileServerFileId: string;
        success: boolean;
        error?: string;
    }): void {
        const upload = this.uploads.get(message.fileServerFileId);
        if (upload) {
            if (message.success) {
                this.markUploadCompleted(message.fileServerFileId);
            } else {
                this.markUploadFailed(message.fileServerFileId, message.error || 'Upload failed');
            }
        }
    }

    setTimeoutForUpload(
        fileServerFileId: string,
        timeoutInMilliseconds: number,
        processingStateTimeoutCallback?: () => void
    ): void {
        if (!processingStateTimeoutCallback) {
            return;
        }

        this.clearTimeout(fileServerFileId);

        const timeoutId = setTimeout(() => {
            const currentUpload = this.uploads.get(fileServerFileId);
            if (currentUpload && currentUpload.status === 'processing') {
                processingStateTimeoutCallback?.();
            }
            this.timeouts.delete(fileServerFileId);
        }, timeoutInMilliseconds);

        this.timeouts.set(fileServerFileId, timeoutId);
    }

    private clearTimeout(fileServerFileId: string): void {
        const timeoutId = this.timeouts.get(fileServerFileId);
        if (timeoutId) {
            clearTimeout(timeoutId);
            this.timeouts.delete(fileServerFileId);
        }
    }
}
